/**
 * SEO metadata — keep CANONICAL_URL aligned with production (https://www.tydii.io).
 * index.html, robots.txt, and sitemap.xml should use the same base URL.
 */
export const CANONICAL_URL = "https://www.tydii.io";
export const SITE_NAME = "TYDII";
export const DEFAULT_TITLE =
  "TYDII – Laundry, Dry Cleaning & Tailoring Delivered";
export const DEFAULT_DESCRIPTION =
  "Tydii is a simple way to get your laundry, dry cleaning, and tailoring done without leaving your home. We connect you to trusted local laundromats, dry cleaners, and tailors. Schedule a pickup and get clean clothes delivered to your door.";
export const OG_IMAGE_URL = `${CANONICAL_URL}/logo512.png`;
export const KEYWORDS =
  "laundry delivery, dry cleaning, tailoring, wash and fold, on-demand laundry, Tydii, Boston laundry, Cambridge laundry, laundry pickup and delivery";

/**
 * Public endpoints — no login required; marketing header layout.
 * Auth routes (/auth/*) are also treated as pre-auth in AppMain but listed separately.
 */
export const PUBLIC_ENDPOINTS = [
  "/",
  "/support",
  "/marketing",
  "/privacy-policy",
  "/delete-account",
  "/laundry-business",
  "/delivery-partner",
];

/** @deprecated Use PUBLIC_ENDPOINTS */
export const PUBLIC_MARKETING_ROUTES = PUBLIC_ENDPOINTS;

function normalizePath(pathname) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/$/, "") || "/";
}

/** True for all public marketing/legal pages (no auth gate). */
export function isPublicEndpoint(pathname) {
  const path = normalizePath(pathname);
  if (PUBLIC_ENDPOINTS.includes(path)) return true;
  return (
    path.startsWith("/laundry-business") ||
    path.startsWith("/delivery-partner")
  );
}

/** @deprecated Use isPublicEndpoint */
export const isPublicMarketingRoute = isPublicEndpoint;

/** Public marketing routes included in sitemap.xml */
export const SITEMAP_PATHS = [
  { path: "/", changefreq: "weekly", priority: "1.0" },
  { path: "/marketing", changefreq: "weekly", priority: "0.9" },
  { path: "/laundry-business", changefreq: "monthly", priority: "0.8" },
  { path: "/delivery-partner", changefreq: "monthly", priority: "0.8" },
  { path: "/support", changefreq: "monthly", priority: "0.7" },
  { path: "/privacy-policy", changefreq: "yearly", priority: "0.4" },
];

export const PAGE_SEO = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/marketing": {
    title: "Laundry Made Simple | TYDII",
    description:
      "Fast, reliable, on-demand laundry, dry cleaning, and tailoring. Get clean clothes delivered to your door. Schedule your first pickup with Tydii.",
  },
  "/laundry-business": {
    title: "Partner With Tydii | Laundry Business",
    description:
      "Grow your laundromat, dry cleaner, or tailoring shop with Tydii. Get more orders, pickup and delivery support, and new customers in your area.",
  },
  "/delivery-partner": {
    title: "Become a Delivery Partner | TYDII",
    description:
      "Drive with Tydii and earn on your schedule. Pick up and deliver laundry orders for local businesses with flexible routes and reliable payouts.",
  },
  "/support": {
    title: "Help & Support | TYDII",
    description:
      "Get help with your Tydii account, orders, and delivery. Contact support, view FAQs, and find answers.",
  },
  "/privacy-policy": {
    title: "Privacy Policy & Terms | TYDII",
    description:
      "Read Tydii's privacy policy, terms of service, and legal information for customers, drivers, and business partners.",
  },
  "/delete-account": {
    title: "Delete Account | TYDII",
    description:
      "Request permanent deletion of your Tydii account. Sign in to confirm and remove your profile and access.",
    noindex: true,
  },
  "/delete-account": {
    title: "Delete Account | TYDII",
    description:
      "Request permanent deletion of your Tydii account. Sign in to confirm and remove your profile and access.",
  },
};

export function getCanonicalForPath(pathname) {
  const path = pathname === "/" ? "" : pathname.replace(/\/$/, "") || "";
  return `${CANONICAL_URL}${path}`;
}

export function getSeoForPath(pathname) {
  const path = pathname === "/" ? "/" : pathname.replace(/\/$/, "") || "/";
  return PAGE_SEO[path] || { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION };
}

export function setMetaContent(selector, content) {
  if (!content) return;
  let el = document.querySelector(selector);
  if (!el) {
    const isProperty = selector.includes("[property=");
    el = document.createElement("meta");
    if (isProperty) {
      const match = selector.match(/\[property="([^"]+)"\]/);
      if (match) el.setAttribute("property", match[1]);
    } else {
      const match = selector.match(/\[name="([^"]+)"\]/);
      if (match) el.setAttribute("name", match[1]);
    }
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setCanonicalLink(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function applyPageSeo(pathname) {
  const seo = getSeoForPath(pathname);
  const canonical = getCanonicalForPath(pathname);
  const robots = seo.noindex ? "noindex, nofollow" : "index, follow";

  document.title = seo.title;

  setMetaContent('meta[name="description"]', seo.description);
  setMetaContent('meta[name="robots"]', robots);
  setMetaContent('meta[name="googlebot"]', robots);

  setCanonicalLink(canonical);

  setMetaContent('meta[property="og:type"]', "website");
  setMetaContent('meta[property="og:url"]', canonical);
  setMetaContent('meta[property="og:title"]', seo.title);
  setMetaContent('meta[property="og:description"]', seo.description);
  setMetaContent('meta[property="og:image"]', OG_IMAGE_URL);
  setMetaContent('meta[property="og:site_name"]', SITE_NAME);

  setMetaContent('meta[name="twitter:card"]', "summary_large_image");
  setMetaContent('meta[name="twitter:url"]', canonical);
  setMetaContent('meta[name="twitter:title"]', seo.title);
  setMetaContent('meta[name="twitter:description"]', seo.description);
  setMetaContent('meta[name="twitter:image"]', OG_IMAGE_URL);
}
